import {Injectable}                  from '@angular/core';
import {SensorStore}                 from '../../stores/sensor/sensor.store';
import {NdiStore}                    from '../../stores/ndi/ndi.store';
import {LicenseService}              from './license/license.service';
import {ApDateService, DateDiffType} from '../../ap-core/services/ap-date-service';
import {NdiService}                  from '../../field-management/services/ndi.service';
import {FieldStore}                  from '../../stores/farm/field.store';
import {BestScanData, BestScanType}  from '../../types/scan.types';
import {ApGetCropService}            from '../../stores/services/ap-get-crop.service';
import {ApSensorDataService}         from '../../stores/services/ap-sensor-data.service';
import {IGridStatus}                 from '../../ap-interface/interfaces/ap-grid-status.interface';
import {SensorPlanBookType}          from '../../types/sensor.types';
import INdiStatistic = Data.NDI.INdiStatistic;
import IField = Data.FieldManagement.IField;
import ISensorFieldData = Data.Sensor.ISensorFieldData;
import ScanType = Data.NDI.Enums.ScanType;

/**
 * Service for managing and retrieving scan data from different sources
 */
@Injectable({providedIn: 'root'})
export class ScanService {
  constructor(private ndiStore: NdiStore,
              private fieldStore: FieldStore,
              private sensorStore: SensorStore,
              private ndiService: NdiService,
              private dateService: ApDateService,
              private cropService: ApGetCropService,
              private licenseService: LicenseService,
              private sensorDataService: ApSensorDataService) {
  }

  /**
   * Gets the best available scan data for a field by comparing sensor and NDI data
   * @param field Field to get scan data for
   * @param defaultScanStatus Optional default status to use if no scan data available
   * @returns Best available scan data or default if none found
   * @private
   */
  public getBestScanData(field: IField | undefined, defaultScanStatus?: IGridStatus): BestScanData | undefined {
    if (!field) {
      return this.defaultBestScanData(defaultScanStatus);
    }
    const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
    if (!fieldGeom) {
      return this.defaultBestScanData(defaultScanStatus);
    }
    const sensorFieldData = this.sensorStore.SensorFieldData.find(x => x.FieldGeomId === fieldGeom.Id);
    const ndiScanData = this._getNdiScanData(field);
    if (!!sensorFieldData && !!ndiScanData) {
      const daysDiff = this.dateService.getDateDiff(new Date(ndiScanData.Created),
        new Date(sensorFieldData.Created), DateDiffType.Days);
      if (daysDiff > 5) {
        return this.getBestScanObjectFromNdiScan(ndiScanData, field);
      }
      return this.getBestScanObjectFromSensor(sensorFieldData);
    }
    if (ndiScanData && !sensorFieldData) {
      return this.getBestScanObjectFromNdiScan(ndiScanData, field);
    }
    if (sensorFieldData && !ndiScanData) {
      return this.getBestScanObjectFromSensor(sensorFieldData);
    }
    return this.defaultBestScanData(defaultScanStatus);
  }

  /**
   * Retrieves NDI scan data for a field if available and valid
   * Checks license validity and data completeness
   * @param field Field to get NDI scan data for
   * @returns NDI statistics if available and valid, undefined otherwise
   * @private
   */
  private _getNdiScanData(field: IField): INdiStatistic | undefined {
    const fieldGeom = this.fieldStore.getCurrentFieldGeom(field);
    const fieldCrop = this.cropService.getFieldCrop(field);
    const ndiScanStatistics = this.ndiStore.NdiStatistics.find(x => x.FieldGeomId === fieldGeom.Id);
    if (!ndiScanStatistics) {
      return undefined;
    }
    const satelliteLicense = this.licenseService.getValidSatelliteLicense();
    if (!satelliteLicense) {
      return undefined;
    }
    const nData = this.ndiService.getSatelliteNValues(ndiScanStatistics, fieldCrop);
    if (!Number.isFinite(nData.NUptakeAvg) || !Number.isFinite(nData.NUptakeMin) || !Number.isFinite(nData.NUptakeMax)) {
      return undefined;
    }
    return ndiScanStatistics;
  }

  /**
   * Creates a default scan data object with optional status
   * @param defaultScanStatus Optional status to include
   * @returns Basic scan data object with status
   * @private
   */
  private defaultBestScanData(defaultScanStatus?: IGridStatus): BestScanData {
    return {
      Status: defaultScanStatus,
      Ec: undefined,
      SatId: undefined,
      PlanId: undefined,
      CropTypeId: undefined,
      Created: undefined,
      NUptakeMin: undefined,
      NUptakeMax: undefined,
      NUptakeAvg: undefined,
      ScanType: undefined,
      SatelliteScan: undefined
    } as BestScanData;
  }

  /**
   * Creates a scan data object from sensor field data
   * Includes plan book info and sensor measurements
   * @param sensorData Raw sensor field data
   * @returns Formatted scan data object with sensor values
   * @private
   */
  private getBestScanObjectFromSensor(sensorData: ISensorFieldData): BestScanData {
    const planBookInfo = this.sensorDataService.getPlanBookInfoFromSensorFieldData(sensorData);
    return {
      Status: this.sensorDataService.getStatusSensorFieldData(sensorData),
      Ec: sensorData.Ec,
      SatId: undefined,
      PlanId: planBookInfo.PlanBookId,
      CropTypeId: sensorData.CroptypeId,
      Created: new Date(sensorData.Created),
      NUptakeMin: sensorData.Min,
      NUptakeMax: sensorData.Max,
      NUptakeAvg: sensorData.Avg,
      ScanType: planBookInfo.Type === SensorPlanBookType.NFertilizationPlanBook
        ? BestScanType.NFertilizationSensor
        : BestScanType.PlantProtectionSensor,
      SatelliteScan: undefined
    } as BestScanData;
  }

  /**
   * Creates a scan data object from NDI scan statistics
   * Includes field crop info and satellite measurements
   * @param ndiScanStatistics NDI scan statistics
   * @param field Associated field
   * @returns Formatted a scan data object with NDI values
   * @private
   */
  private getBestScanObjectFromNdiScan(ndiScanStatistics: INdiStatistic, field: IField): BestScanData {
    const fieldCrop = this.cropService.getFieldCrop(field);
    const satelliteNValues = this.ndiService.getSatelliteNValues(ndiScanStatistics, fieldCrop);
    return {
      Status: this.getStatusForNdiScan(ndiScanStatistics),
      Ec: fieldCrop.EcValue,
      SatId: ndiScanStatistics.FieldGeomId,
      PlanId: undefined,
      CropTypeId: fieldCrop.CroptypeId,
      Created: new Date(ndiScanStatistics.Created),
      NUptakeMin: satelliteNValues.NUptakeMin,
      NUptakeMax: satelliteNValues.NUptakeMax,
      NUptakeAvg: satelliteNValues.NUptakeAvg,
      ScanType: BestScanType.SatelliteScan,
      SatelliteScan: ndiScanStatistics.ScanType
    } as BestScanData;
  }

  /**
   * Gets the basic status for an NDI record
   * @param ndiStatistic NDI statistic object
   * @returns Grid status object containing state, class and description
   * @public
   */
  private getStatusForNdiScan(ndiStatistic: INdiStatistic | undefined): IGridStatus {
    switch (ndiStatistic?.ScanType) {
      case ScanType.Regular:
        return {
          state: 1,
          class: 'ap-status-green',
          description: 'Global__NDI_Status_Desc_green'
        };
      case ScanType.Autumn:
        return {
          state: 1,
          class: 'ap-status-green',
          description: 'Global__NDI_Status_Desc_autumn'
        };
      default:
        return {
          state: 2,
          class: 'ap-status-grey',
          description: 'Global__NDI_Status_Desc_grey'
        };
    }
  }
}
